<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type AggregateDialog from '@autobid/ui/components/common/dialog/AggregateRedirectionInfo.vue'
import type { CarsListCar, SearchListCar } from '@autobid/ui/types/Car'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useCarDescription } from '@autobid/ui/composables/car/useCarDescription'
import { useAuctionPagesLink } from '@autobid/ui/composables/useAuctionPagesLink'
import { useIsMobile } from '@autobid/ui/composables/useIsMobile'
import { useOpenLink } from '@autobid/ui/composables/useOpenLink'
import { AGGREGATE_BID_ID } from '@autobid/ui/constants/AGGREGATE_BID_ID'
import { waitFor } from '@autobid/ui/utils/waitFor'
import { computed, inject, onMounted, ref } from 'vue'
import { Vue3SlideUpDown } from 'vue3-slide-up-down'
import CarListTop from './CarListTop.vue'

interface Props {
  car: CarsListCar | SearchListCar
  isFirst: boolean
}

const props = defineProps<Props>()

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')

const { open } = useOpenLink()
const { isAuthed } = useAutobidAuth()
const { getDescription } = useCarDescription()
const desc = computed(() => {
  return getDescription(props.car)
})
const descActive = ref(false)
const initialized = ref(false)

const { getLink } = useAuctionPagesLink(props.car.appId)
const barometerUrl = computed(() =>
  getLink({
    page: 'barometer',
    slug: props.car.slug,
  }),
)

const isMobile = useIsMobile()

const aggregateModalRef = ref<InstanceType<typeof AggregateDialog>>()

const shouldBlockTransition = computed(
  () =>
    props.car.appId === AGGREGATE_BID_ID
    && !aggregateModalRef.value?.shouldNotDisplayed,
)

async function onClick() {
  if (shouldBlockTransition.value) {
    await waitFor(() => aggregateModalRef.value != null)

    if (!aggregateModalRef.value) {
      throw new Error('Aggregate modal ref is not defined')
    }

    aggregateModalRef.value.opened = true

    return
  }
  if (barometerUrl.value && props.car.stage !== 'REMOVED') {
    open(barometerUrl.value)
  }
}

onMounted(() => {
  initialized.value = true
})
</script>

<template>
  <div class="car">
    <div
      class="-mx-3 block px-3 pt-3 text-[#58585A] md:hover:bg-[#f1f5f9]"
      :class="{ 'cursor-pointer': car.stage !== 'REMOVED' }"
      @click="onClick"
    >
      <span
        v-if="auctionStrapiData?.type === 'carList' || !isFirst"
        class="mb-3 flex items-center whitespace-nowrap after:h-px after:w-full after:bg-accordion-primary"
      >
        <CarListTop
          v-if="auctionStrapiData?.type === 'carList' && 'hotbidDuration' in car"
          :car="car"
        />
      </span>

      <div class="relative z-10 flex flex-wrap md:flex-nowrap">
        <div
          class="mr-3 flex w-full flex-col-reverse md:block md:w-1/2 md:min-w-[50%] xl:w-48 xl:min-w-[12rem]"
        >
          <div class="flex w-full items-start gap-3">
            <DynamicAuctionItemListCarThumbnail :car="car" />

            <div
              v-if="car.stage !== 'REMOVED'"
              class="flex w-1/2 flex-col-reverse md:hidden"
            >
              <DynamicAuctionItemListCarPrice :car="car" />
            </div>
          </div>

          <div
            v-if="car.stage !== 'REMOVED'"
            class="mb-2 flex items-end gap-1 md:mb-0 md:flex-row md:items-start md:justify-start"
            :class="{
              'flex-row-reverse justify-between': isAuthed,
            }"
          >
            <div v-if="isAuthed" class="flex gap-1">
              <ElementsCarDynamicActions :car="car" size="dynamic" />
            </div>

            <ElementsCarLocationInfo
              :car="car"
              class="flex flex-col items-center md:hidden"
              :class="{
                'mx-auto -translate-x-4': !isAuthed,
                'absolute left-[50%] -ml-4 translate-x-[-50%]': isAuthed,
              }"
            />

            <div
              v-if="isAuthed"
              class="flex flex-row-reverse gap-1 md:flex-row md:flex-wrap"
            >
              <ElementsCarStaticActions
                :sca-popup-slug="auctionStrapiData?.scaPopupSlug"
                :car="car"
                size="small"
              />
            </div>
          </div>
        </div>

        <div class="w-full">
          <p
            v-if="car.stage === 'REMOVED'"
            class="font-bold uppercase text-red"
          >
            {{ $t('cars-list.car-removed-info') }}
          </p>

          <p
            v-else
            class="mb-2 flex items-center justify-between font-bold md:block"
          >
            <common-link
              :href="barometerUrl"
              target="_self"
              underline
              class="!text-inherit"
              @click="onClick"
            >
              {{ car.name }}
            </common-link>

            <span
              class="relative ml-1.5 inline-block h-7 w-7 min-w-[28px] rounded-full bg-[#AFAFAF] pl-2 text-center md:hidden"
            >
              <Icon
                mode="css"
                size="28"
                name="icon-park-outline:right"
                class="absolute left-0 top-0 text-white"
              />
            </span>
          </p>

          <span
            class="relative flex flex-wrap overflow-x-hidden xl:flex-nowrap xl:justify-between"
          >
            <DynamicAuctionItemListCarGeneralInfo
              :car="car"
              :desc-active="descActive"
              @toggle-desc="descActive = !descActive"
            />

            <DynamicAuctionItemListCarAdditionalInfo
              v-if="car.stage !== 'REMOVED'"
              :car="car"
              class="hidden xl:flex"
            />
          </span>

          <span v-show="isMobile" class="block">
            <Vue3SlideUpDown v-model="descActive" :duration="250">
              <div v-show="initialized" class="text-sm">
                {{ desc }}
              </div>
            </Vue3SlideUpDown>
          </span>
          <p v-show="!isMobile" class="mt-4">
            {{ desc }}
          </p>
        </div>
      </div>

      <div class="mt-2 hidden md:flex xl:hidden">
        <DynamicAuctionItemListCarAdditionalInfo
          v-if="car.stage !== 'REMOVED'"
          :car="car"
          class="hidden md:flex"
        />
      </div>

      <DynamicAuctionItemListCarAuctionInfo
        v-if="isMobile"
        :car="car"
        class="relative z-20 bg-white duration-300 md:hidden"
        :class="{ '-mt-3': !descActive, 'mt-2': descActive }"
      />
    </div>

    <LazyCommonDialogAggregateRedirectionInfo
      v-if="barometerUrl"
      ref="aggregateModalRef"
      :link="barometerUrl"
    />
  </div>
</template>

<style lang="scss" scoped>
.auction-label {
  height: 22px;

  &::after {
    width: 16px;
    height: 16px;
    top: 3px;
    right: -9px;
  }
}

.car {
  --swiper-pagination-color: #fff;
  --swiper-pagination-bullet-inactive-color: #fff;
  --swiper-pagination-bullet-inactive-opacity: 0.4;
}
</style>
